/**
 * ``CoverageLayer`` — non-visual React component that renders every
 * platform's **coverage** rings as GeoJSON fills + outlines (PR5b inc4;
 * REQ-UI-070; ADR-032 §4.2). The coverage sibling of :func:`FovLayer`; both
 * (and :func:`ObscurationLayer`) share the geometry store, the toggle hook,
 * and the gated-effect lifecycle.
 *
 * Coverage is a **list** of rings per platform, so a single platform may
 * contribute several polygon features (one per ring) — see
 * :func:`coverageToFeatureCollection`. Otherwise the behaviour mirrors
 * :func:`FovLayer` exactly:
 *   1. read the per-platform geometry from the shared geometry store
 *      (:func:`usePlatformGeometry`), READ-ONLY (the store's SSE bridge is
 *      owned by :file:`use-map-sse-bridges.ts`);
 *   2. gate rendering behind the operator's ``coverageVisible`` toggle AND
 *      ``zoom >= GEOMETRY_MIN_ZOOM`` (the shared perf gate);
 *   3. attach / update in place when the gate is open; detach when it closes.
 *
 * (2)+(3) are delegated to the shared :func:`useGeometryLayerEffect`
 * (CLAUDE.md §4.4 — no duplicated gate/listener logic across the three
 * geometry layers). This component binds only the coverage-specific pieces:
 * the ``coverageVisible`` flag and the coverage attach/detach.
 *
 * Cross-references:
 *   - REQ-UI-070 (off-by-default, zoom-gated geometry) / REQ-UI-002 (WebGL).
 *   - ADR-032 §4.2 (Status geometry data model — coverage ring list).
 *   - CLAUDE.md §5.8 (perf discipline) / §8.1 (operator visibility) /
 *     §4.4 (no duplicated logic).
 *   - map/geometry-layer-effect.ts (the shared gated lifecycle).
 *   - map/geometry-store.ts · map/use-geometry-layers.ts ·
 *     map/coverage-layer-impl.ts.
 */
import type { Map as MapLibreMap } from "maplibre-gl";
import { usePlatformGeometry } from "@/map/geometry-store";
import { useGeometryVisibility } from "@/map/use-geometry-layers";
import { useGeometryLayerEffect } from "@/map/geometry-layer-effect";
import { attachCoverageLayer, detachCoverageLayer } from "@/map/coverage-layer-impl";

/** Props for :func:`CoverageLayer`. */
export interface CoverageLayerProps {
  /** Live MapLibre map instance (mounted after the map's ``load``). */
  map: MapLibreMap;
}

/**
 * Render + gate the coverage polygon layer. Returns no DOM (WebGL-only).
 */
export function CoverageLayer({ map }: CoverageLayerProps): null {
  // Read-only: the geometry store is fed by the status-geometry SSE bridge
  // in useMapSseBridges (see module docstring). We subscribe + paint.
  const entries = usePlatformGeometry();
  // Visibility is the active Profile's map.visible_layers.coverage, falling
  // back to false off-by-default (REQ-UI-089 / REQ-UI-070). Single source —
  // the per-session override layer was removed with the map cog menu (item
  // 3a).
  const config = useGeometryVisibility();

  // The shared gated lifecycle: subscribe to zoom, attach when
  // ``coverageVisible && zoom >= GEOMETRY_MIN_ZOOM`` else detach.
  useGeometryLayerEffect({
    map,
    entries,
    visible: config.coverageVisible,
    attach: attachCoverageLayer,
    detach: detachCoverageLayer,
  });

  return null;
}
